/**
 * A1 — Pestañas de lista de la ficha del cliente: Expedientes, Documentos,
 * Presupuestos y Comunicación. Cada una pide solo lo de ESE cliente (el filtro
 * `clientId` ya existe en las listas globales, que se mantienen como vistas
 * transversales del bufete).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  Download,
  FileText,
  Loader2,
  Mail,
  Paperclip,
  Plus,
  Scale,
  Upload,
} from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import NewCaseModal from '../cases/NewCaseModal'
import { UploadModal } from '../cases/DocumentsPanel'
import { QuoteStatusBadge } from '../../pages/Quotes'
import {
  CaseStatusLabel,
  DocumentCategoryLabel,
  LegalAreaLabel,
  type CaseListResponse,
  type DocumentListItem,
  type QuoteListResponse,
} from '@toga/shared'

// ---------- piezas comunes ----------

function useFetch<T>(path: string) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    setData(null)
    setError(null)
    api
      .get<T>(path)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [path, tick])

  return { data, error, reload: useCallback(() => setTick((t) => t + 1), []) }
}

function Card({
  title,
  icon,
  action,
  children,
}: {
  title: string
  icon: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="surface overflow-hidden">
      <header className="px-5 py-3.5 border-b border-base flex items-center justify-between gap-3">
        <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
          {icon}
          {title}
        </h3>
        {action}
      </header>
      {children}
    </section>
  )
}

function Status({ error, loading }: { error: string | null; loading: boolean }) {
  if (error) {
    return (
      <div className="m-4 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
        <AlertCircle size={13} /> {error}
      </div>
    )
  }
  if (loading) {
    return (
      <div className="px-5 py-6 t-muted text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    )
  }
  return null
}

const Empty = ({ children }: { children: ReactNode }) => (
  <p className="px-5 py-8 text-sm t-faint text-center">{children}</p>
)

const th = 'px-5 py-2 text-left text-[11px] uppercase tracking-wider t-faint font-medium'
const td = 'px-5 py-2.5'
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('es-ES')

const primaryBtn =
  'px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5'

// ---------- Expedientes ----------

export function ClientCasesTab({
  clientId,
  canCreate,
  onChanged,
}: {
  clientId: string
  canCreate: boolean
  onChanged: () => void
}) {
  const navigate = useNavigate()
  const { data, error } = useFetch<CaseListResponse>(`/cases?clientId=${clientId}&pageSize=100`)
  const [creating, setCreating] = useState(false)

  return (
    <Card
      title="Expedientes"
      icon={<Scale size={15} className="text-toga-300" />}
      action={
        canCreate && (
          <button type="button" onClick={() => setCreating(true)} className={primaryBtn}>
            <Plus size={13} /> Nuevo expediente
          </button>
        )
      }
    >
      <Status error={error} loading={!data && !error} />
      {data &&
        (data.items.length === 0 ? (
          <Empty>Este cliente aún no tiene expedientes.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base">
                  <th className={th}>Código</th>
                  <th className={th}>Título</th>
                  <th className={th}>Área</th>
                  <th className={th}>Estado</th>
                  <th className={th}>Apertura</th>
                  <th className={`${th} text-right`}>Docs</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-b border-base/50 last:border-0 hover:bg-hover/50">
                    <td className={`${td} font-mono text-xs whitespace-nowrap`}>
                      <Link to={`/cases/${c.id}`} className="text-toga-300 hover:underline">
                        {c.code}
                      </Link>
                    </td>
                    <td className={`${td} t-primary`}>{c.title}</td>
                    <td className={`${td} t-secondary text-xs`}>{LegalAreaLabel[c.area]}</td>
                    <td className={`${td} t-secondary text-xs`}>{CaseStatusLabel[c.status]}</td>
                    <td className={`${td} t-muted text-xs`}>{fmtDate(c.openedAt)}</td>
                    <td className={`${td} t-muted text-xs text-right`}>{c.documentsCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {creating && (
        <NewCaseModal
          defaultClientId={clientId}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false)
            onChanged()
            navigate(`/cases/${created.id}`)
          }}
        />
      )}
    </Card>
  )
}

// ---------- Documentos ----------

interface ClientDoc extends DocumentListItem {
  caseCode?: string | null
  caseTitle?: string | null
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function ClientDocumentsTab({
  clientId,
  canWrite,
  onChanged,
}: {
  clientId: string
  canWrite: boolean
  onChanged: () => void
}) {
  // El filtro por cliente del servidor ya incluye los documentos de sus expedientes.
  const { data, error, reload } = useFetch<{ items: ClientDoc[]; total: number }>(
    `/documents?clientId=${clientId}&pageSize=100`,
  )
  const [uploading, setUploading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const onDownload = async (d: ClientDoc) => {
    setDownloadError(null)
    try {
      const { url } = await api.get<{ url: string }>(
        `/documents/${d.id}/versions/${d.latestVersion}/download`,
      )
      window.open(url, '_blank')
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'No se pudo descargar')
    }
  }

  return (
    <Card
      title="Documentos"
      icon={<FileText size={15} className="text-toga-300" />}
      action={
        canWrite && (
          <button type="button" onClick={() => setUploading(true)} className={primaryBtn}>
            <Upload size={13} /> Subir documento
          </button>
        )
      }
    >
      <Status error={error ?? downloadError} loading={!data && !error} />
      {data &&
        (data.items.length === 0 ? (
          <Empty>
            Sin documentos. Aquí aparecen los del cliente y los de todos sus expedientes; para
            añadir uno a un expediente concreto, ábrelo desde la pestaña Expedientes.
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base">
                  <th className={th}>Archivo</th>
                  <th className={th}>Pertenece a</th>
                  <th className={th}>Categoría</th>
                  <th className={th}>Fecha</th>
                  <th className={`${th} text-right`}>Tamaño</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.id} className="border-b border-base/50 last:border-0 hover:bg-hover/50">
                    <td className={`${td} t-primary`}>
                      <span className="truncate inline-block max-w-[300px] align-bottom" title={d.fileName}>
                        {d.fileName}
                      </span>
                    </td>
                    <td className={`${td} text-xs`}>
                      {d.caseId && d.caseCode ? (
                        <Link to={`/cases/${d.caseId}`} className="text-toga-300 hover:underline">
                          <span className="font-mono">{d.caseCode}</span>
                        </Link>
                      ) : (
                        <span className="t-secondary">Cliente</span>
                      )}
                    </td>
                    <td className={`${td} t-secondary text-xs`}>{DocumentCategoryLabel[d.category]}</td>
                    <td className={`${td} t-muted text-xs`}>{fmtDate(d.createdAt)}</td>
                    <td className={`${td} t-muted text-xs text-right font-mono`}>
                      {formatSize(d.latestSize)}
                    </td>
                    <td className={`${td} text-right`}>
                      <button
                        type="button"
                        onClick={() => void onDownload(d)}
                        title="Descargar"
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-faint hover:text-toga-300"
                      >
                        <Download size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {uploading && (
        <UploadModal
          clientId={clientId}
          initialFile={null}
          onClose={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false)
            reload()
            onChanged()
          }}
        />
      )}
    </Card>
  )
}

// ---------- Presupuestos ----------

export function ClientQuotesTab({ clientId, canWrite }: { clientId: string; canWrite: boolean }) {
  const navigate = useNavigate()
  const { data, error } = useFetch<QuoteListResponse>(`/quotes?clientId=${clientId}&pageSize=100`)

  return (
    <Card
      title="Presupuestos"
      icon={<FileText size={15} className="text-toga-300" />}
      action={
        canWrite && (
          <button
            type="button"
            onClick={() => navigate(`/quotes/new?clientId=${clientId}`)}
            className={primaryBtn}
          >
            <Plus size={13} /> Nuevo presupuesto
          </button>
        )
      }
    >
      <Status error={error} loading={!data && !error} />
      {data &&
        (data.items.length === 0 ? (
          <Empty>Sin presupuestos para este cliente.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base">
                  <th className={th}>Número</th>
                  <th className={th}>Concepto</th>
                  <th className={th}>Estado</th>
                  <th className={th}>Fecha</th>
                  <th className={th}>Expediente</th>
                  <th className={`${th} text-right`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((q) => (
                  <tr key={q.id} className="border-b border-base/50 last:border-0 hover:bg-hover/50">
                    <td className={`${td} font-mono text-xs whitespace-nowrap`}>
                      <Link to={`/quotes/${q.id}`} className="text-toga-300 hover:underline">
                        {q.number}
                      </Link>
                    </td>
                    <td className={`${td} t-primary`}>{q.title ?? '—'}</td>
                    <td className={td}>
                      <QuoteStatusBadge status={q.status} />
                    </td>
                    <td className={`${td} t-muted text-xs`}>{fmtDate(q.issueDate)}</td>
                    <td className={`${td} t-secondary text-xs font-mono`}>{q.caseCode ?? '—'}</td>
                    <td className={`${td} t-secondary text-right font-mono`}>
                      {formatCents(q.totalCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </Card>
  )
}
